import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestConfig
} from 'axios';
import notify from './notify';

type UnwrapApiData<T> = T extends { data: infer U } ? U : T;

interface IHeaders {
  [key: string]: string;
}

interface IAxiosPluginConfig {
  baseURL: string;
  headers?: IHeaders;
  token?: string;
  getToken?: () => string | undefined | null;
  onTokenUpdate?: (token: string, response: AxiosResponse<any>) => void | Promise<void>;
  onUnauthorized?: (error: AxiosError) => void | Promise<void>;
}

class Http {
  public instance: AxiosInstance;

  private getToken?: () => string | undefined | null;
  private onTokenUpdate?: (token: string, response: AxiosResponse<any>) => void | Promise<void>;
  private onUnauthorized?: (error: AxiosError) => void | Promise<void>;

  constructor(config: IAxiosPluginConfig) {
    this.getToken = config.getToken;
    this.onTokenUpdate = config.onTokenUpdate;
    this.onUnauthorized = config.onUnauthorized;

    this.instance = axios.create({
      baseURL: config.baseURL,
      headers: {
        client: 'web',
        'Content-Type': 'application/json',
        ...(config.headers || {}),
        clientTimeZone: (new Date().getTimezoneOffset() / 60) * -1
      }
    });

    if (config.token) {
      this.setAuthToken(config.token);
    }

    this.setupInterceptors();
  }

  public setAuthToken(token: string): void {
    this.instance.defaults.headers.acx = token;
  }

  public removeAuthToken(): void {
    delete this.instance.defaults.headers.acx;
  }

  private setupInterceptors(): void {
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const tokenFromDefaults = this.instance.defaults.headers.acx as string | undefined;
        const tokenFromGetter = this.getToken?.();

        config.headers = config.headers || {};
        config.headers.acx = tokenFromDefaults ?? tokenFromGetter ?? '';

        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      async (response: AxiosResponse<any>) => {
        const updatedToken = response.headers?.acx as string | undefined;

        if (updatedToken) {
          this.setAuthToken(updatedToken);
          await this.onTokenUpdate?.(updatedToken, response);
        }

        if (response.status !== 200 && response.data?.Message) {
          this.handleErrorResponse(response);
        }

        if (!response.data?.isSucceed && response.data?.error?.length) {
          this.handleErrorResponse(response);
        }

        return response;
      },
      async (error: AxiosError) => {
        await this.handleRequestError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleErrorResponse(response: AxiosResponse<any>): void {
    notify.show({
      type: 'error',
      title: 'Ошибка',
      text: response.data?.Message || response.data?.error
    });
  }

  private async handleRequestError(error: AxiosError): Promise<void> {
    if (error.response?.status === 401) {
      await this.onUnauthorized?.(error);
      return;
    }

    if (error.response) {
      console.error('Ошибка в ответе запроса:', error.response);
    } else {
      console.error('Ошибка запроса:', error);
    }
  }

  public setBaseURL(baseURL: string): void {
    this.instance.defaults.baseURL = baseURL;
  }

  public setHeaders(headers: Record<string, string>): void {
    this.instance.defaults.headers = {
      ...this.instance.defaults.headers,
      ...headers
    };
  }

  public get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<UnwrapApiData<T>>> {
    return this.instance.get<any, AxiosResponse<UnwrapApiData<T>>>(url, config);
  }

  public post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<UnwrapApiData<T>>> {
    return this.instance.post<any, AxiosResponse<UnwrapApiData<T>>>(url, data, config);
  }

  public put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<UnwrapApiData<T>>> {
    return this.instance.put<any, AxiosResponse<UnwrapApiData<T>>>(url, data, config);
  }

  public delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<UnwrapApiData<T>>> {
    return this.instance.delete<any, AxiosResponse<UnwrapApiData<T>>>(url, config);
  }
}

export default Http;